import { Box, Tooltip } from '@chakra-ui/react';
import { t } from 'i18next';
import { SubscriptionErrorIcon, SubscriptionWarningIcon } from '~components/ui/icons';

/**
 * The 4 subscription tag variants:
 * - Essential: active subscription with valid date and available seats (blue)
 * - EssentialWarning: expiring soon (<3 months) or insufficient seats (orange + ⚠ icon)
 * - EssentialExpired: subscription past validUntil date (red + error icon)
 * - Lite: no active subscription (gray)
 */
export enum SubscriptionTagVariant {
  Essential = 'Essential',
  EssentialWarning = 'EssentialWarning',
  EssentialExpired = 'EssentialExpired',
  EssentialInsufficient = 'EssentialInsufficient',
  Lite = 'Lite',
}

interface TagStyle {
  label: string;
  bg: string;
  color: string;
  icon: React.ReactNode | null;
  tooltip: string;
}

const TAG_STYLES: Record<SubscriptionTagVariant, TagStyle> = {
  [SubscriptionTagVariant.Essential]: {
    label: 'Essential',
    bg: 'blue.50',
    color: 'blue.500',
    icon: null,
    tooltip: t('essential'),
  },
  [SubscriptionTagVariant.EssentialWarning]: {
    label: 'Essential',
    bg: '#FFF3E8',
    color: '#E67635',
    icon: <SubscriptionWarningIcon />,
    tooltip: t('essentialExpiringSoon'),
  },
  [SubscriptionTagVariant.EssentialExpired]: {
    label: 'Essential',
    bg: '#FEECEC',
    color: '#EF4E4E',
    icon: <SubscriptionErrorIcon />,
    tooltip: t('essentialExpired'),
  },
  [SubscriptionTagVariant.Lite]: {
    label: 'Lite',
    bg: 'gray.50',
    color: 'gray.500',
    icon: null,
    tooltip: t('lite'),
  },
  [SubscriptionTagVariant.EssentialInsufficient]: {
    label: 'Essential',
    bg: '#FEECEC',
    color: '#EF4E4E',
    icon: <SubscriptionErrorIcon />,
    tooltip: t('essentialInsufficient'),
  },
};

interface Props {
  variant: SubscriptionTagVariant;
}

export function SubscriptionTagV2({ variant }: Props) {
  const { label, bg, color, icon, tooltip } = TAG_STYLES[variant];

  return (
    <Tooltip label={tooltip} hasArrow>
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        gap={icon ? '1' : '0'}
        bg={bg}
        color={color}
        px="3"
        py="1"
        borderRadius="full"
        fontWeight="semibold"
        fontSize="sm"
        minWidth="110px"
      >
        {icon}
        {label}
      </Box>
    </Tooltip>
  );
}
